import React, { FC } from 'react';
import './TransactionForm.css';
import {
	InputField,
	SelectField,
	FaBuilding,
	FaMoneyBillAlt,
	FaSearchDollar,
	FaUser,
	FaFileInvoice,
	RequiredNote,
	DateTimeField,
	DomainFormProps,
	FaBoxOpen,
	SelectOptionType,
	SEARCH_PROPERTY_RENT_MANADTE_TYPE,
	SoNumber
} from '@/Shared';
import { TransactionFormViewModel } from '../../Logic/ViewModels/TransactionFormViewModel';
import { SingleValue } from 'react-select';


/**
 * =====================================================
 * Component: TransactionForm
 * =====================================================
 *
 *
 * Domain-specific form UI for Transaction.
 * Mounted inside GenericDomainForm.
 *
 * Responsibilities:
 * - Render transaction fields
 * - Use data from formVM (options, values)
 *
 * No permissions, no loading, no orchestration logic here.
 *
 * Author: Samano CASTRE
 * Date: February 15, 2026
 */
const TransactionForm: FC<DomainFormProps> = ({
	domainFormHook,
	formRef,
	formikProps,

}) => {

	const { values, setFieldValue } = formikProps;
	const entityFormVM = domainFormHook.entityFormVM as TransactionFormViewModel;

	const updateFields = (computedValues: Record<string, any>) => {
		Object.entries(computedValues).forEach(
			([key, value]) => setFieldValue(key, value, true)
		);
	}

	/**
	 * =====================================================
	 * Main Form Rendering
	 * =====================================================
	 *
	 * Fieldsets organize the form into logical sections for clarity.
	 */
	return (

		<div className="fieldset-wrapper">

			<fieldset>
				<legend>Détails de la Transaction</legend>
				<SelectField
					label='Mandat'
					required
					icon={<FaFileInvoice />}
					name='mandateId'
					options={entityFormVM.mandateOptions}
					scrollParent={formRef.current}
					onChange={
						(selectedOptions) => {
							const value = (selectedOptions as SingleValue<SelectOptionType>)?.value;
							updateFields(entityFormVM.handleMandateChange(value));
						}
					}
				/>
				{values.mandateType && (<>
					<SelectField
						label='Type de mandat'
						scrollParent={formRef.current}
						icon={<FaFileInvoice />}
						name='mandateType'
						options={entityFormVM.mandateTypeOptions}
						placeholder='Type Mandat (Vient du mandat Selectionné)'
						readOnly
					/>

					<SelectField
						label='Bien'
						placeholder='Bien (Vient du mandat Selectionné)'
						options={entityFormVM.propertyOptions}
						icon={<FaBuilding />}
						name='propertyId'
						readOnly
					/>
					{
						values.mandateType === SEARCH_PROPERTY_RENT_MANADTE_TYPE && 
						<SelectField
							label='Besoin'
							placeholder='Besoin (Vient du mandat Selectionné)'
							icon={<FaBoxOpen />}
							name='needId'
							options={entityFormVM.needOptions}
							readOnly
						/>
					}
				</>)
				}

				<RequiredNote />
			</fieldset>
			<fieldset>
				<legend>Dates & Parties prenantes</legend>
				<DateTimeField
					required
					label='Date Transaction'
					dateName='transactionDate'
				/>

				<DateTimeField
					required
					label='Date réservation'
					dateName='bookingDate'
				/>
				<SelectField
					label='Conseillé Transaction'
					required
					icon={<FaUser />}
					name='advisorId'
					options={entityFormVM.userOptions}
					scrollParent={formRef.current}
				/>

				<SelectField
					label='Client'
					required
					icon={<FaUser />}
					name='clientId'
					options={entityFormVM.clientOptions}
					scrollParent={formRef.current}
				/>
			</fieldset>
			<fieldset>
				<legend>Prix & Charges</legend>

				<InputField
					required
					label='Loyers Prix Vente'
					icon={<FaMoneyBillAlt />}
					name='operationPrice'
					type='text'
					autoComplete='on' 
					onChange={ 
						(e) => setFieldValue(
							'operationPrice', 
							SoNumber.formatAmount(e.target.value)
						)
					}
				/>

				<InputField
					required
					label='Provisions / Charges'
					icon={<FaMoneyBillAlt />}
					name='serviceChargeProvision'
					type='text'
					autoComplete='on' 
					onChange={ 
						(e) => setFieldValue(
							'serviceChargeProvision', 
							SoNumber.formatAmount(e.target.value)
						)
					}
				/>

				<InputField
					label='Entretien Climatisation'
					icon={<FaMoneyBillAlt />}
					name='acMaintenanceCharge'
					type='text'
					autoComplete='on' 
					onChange={ 
						(e) => setFieldValue(
							'acMaintenanceCharge', 
							SoNumber.formatAmount(e.target.value)
						)
					}
				/>

				<InputField
					required
					label='Dépôt de garantie'
					icon={<FaMoneyBillAlt />}
					name='securityDeposit'
					type='text'
					autoComplete='on' 
					onChange={ 
						(e) => setFieldValue(
							'securityDeposit', 
							SoNumber.formatAmount(e.target.value)
						)
					}
				/>

				<RequiredNote />
			</fieldset>
			<fieldset>
				<legend>Commission & Frais</legend>
				<InputField
					required
					label='Taux Commission'
					icon={<FaSearchDollar />}
					name='comRate'
					type='number'
					autoComplete='on' 
				/>

				<InputField
					required
					label='Frais préneur'
					icon={<FaMoneyBillAlt />}
					name='clientCom'
					type='text'
					placeholder='Saisir Frais Preneur'
					autoComplete='on' 
					onChange={ 
						(e) => setFieldValue(
							'clientCom', 
							SoNumber.formatAmount(e.target.value)
						)
					}
				/>

				<InputField
					required
					label='Frais Propriétaire'
					icon={<FaMoneyBillAlt />}
					name='ownerCom'
					type='text'
					placeholder='Saisir Frais Propriétaire'
					autoComplete='on' 
					onChange={ 
						(e) => setFieldValue(
							'ownerCom', 
							SoNumber.formatAmount(e.target.value)
						)
					}
				/>

				<InputField
					required
					label='Frais Agence'
					icon={<FaMoneyBillAlt />}
					name='agencyCom'
					type='text'
					placeholder='Saisir Frais Agence'
					autoComplete='on' 
					onChange={ 
						(e) => setFieldValue(
							'agencyCom', 
							SoNumber.formatAmount(e.target.value)
						)
					}
					/>
				<RequiredNote />
			</fieldset>
		</div>

	);
}

export default TransactionForm;
